/**
 * Vendor Management Component
 * 
 * Allows customers to create and manage their own vendor accounts.
 * Vendors are created by customers and linked to them for repair assignments.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Plus, X, Edit2, Power, PowerOff, Key, Trash2, Copy, Check, Download, Eye, EyeOff, Search } from 'lucide-react';
import api from '../lib/api';
import SearchableSelect from './SearchableSelect';
import FeatureGate from './FeatureGate';

export default function VendorManagement() {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [newCredentials, setNewCredentials] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [formData, setFormData] = useState({
    vendorCompanyName: '',
    vendorEmail: '',
    vendorPhone: '',
    initialPassword: ''
  });

  const [resetPasswordData, setResetPasswordData] = useState({
    newPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load vendors on mount
  useEffect(() => {
    loadVendors();
  }, []);

  const loadVendors = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/vendors');
      setVendors(response);
    } catch (error) {
      console.error('Error loading vendors:', error);
      alert('Failed to load vendors. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData(prev => ({ ...prev, initialPassword: password }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.vendorCompanyName.trim()) {
      newErrors.vendorCompanyName = 'Company name is required';
    }

    if (!formData.vendorEmail.trim()) {
      newErrors.vendorEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.vendorEmail)) {
      newErrors.vendorEmail = 'Invalid email format';
    }

    if (!formData.initialPassword) {
      newErrors.initialPassword = 'Password is required';
    } else if (formData.initialPassword.length < 8) {
      newErrors.initialPassword = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      console.log('Creating vendor with data:', formData);
      const response = await api.post('/api/vendors', formData);
      console.log('Vendor creation response:', response);
      
      // Show credentials modal
      setNewCredentials(response.credentials);
      setShowCredentialsModal(true);
      
      // Close add modal and reset form
      setShowAddModal(false);
      setFormData({
        vendorCompanyName: '',
        vendorEmail: '',
        vendorPhone: '',
        initialPassword: ''
      });
      setErrors({});

      // Reload vendors
      await loadVendors();
    } catch (error) {
      console.error('Error creating vendor:', error);
      console.error('Error details:', {
        message: error.message,
        data: error.data,
        status: error.status
      });
      
      // Show detailed error message
      const errorMessage = error.data?.message || error.message || 'Failed to create vendor. Please try again.';
      alert(`Error creating vendor: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivate = async (vendor) => {
    if (!window.confirm(`Deactivate ${vendor.vendor_company_name}? They will not be able to log in.`)) {
      return;
    }

    try {
      await api.post(`/api/vendors/${vendor.relationship_id}/deactivate`);
      await loadVendors();
      alert('Vendor deactivated successfully.');
    } catch (error) {
      console.error('Error deactivating vendor:', error);
      alert('Failed to deactivate vendor. Please try again.');
    }
  };

  const handleActivate = async (vendor) => {
    try {
      await api.post(`/api/vendors/${vendor.relationship_id}/activate`);
      await loadVendors();
      alert('Vendor activated successfully.');
    } catch (error) {
      console.error('Error activating vendor:', error);
      alert('Failed to activate vendor. Please try again.');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (resetPasswordData.newPassword.length < 8) {
      alert('Password must be at least 8 characters.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await api.post(`/api/vendors/${selectedVendor.relationship_id}/reset-password`, resetPasswordData);
      
      // Show new credentials
      setNewCredentials(response.credentials);
      setShowCredentialsModal(true);
      setShowResetPasswordModal(false);
      setResetPasswordData({ newPassword: '' });
      setSelectedVendor(null);

      alert('Password reset successfully.');
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Failed to reset password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (vendor) => {
    if (!window.confirm(`Delete ${vendor.vendor_company_name}? This cannot be undone and will fail if the vendor has existing repairs.`)) {
      return;
    }

    try {
      await api.delete(`/api/vendors/${vendor.relationship_id}`);
      await loadVendors();
      alert('Vendor deleted successfully.');
    } catch (error) {
      console.error('Error deleting vendor:', error);
      alert(error.response?.data?.message || 'Failed to delete vendor. If they have existing repairs, deactivate instead.');
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const downloadCredentials = () => {
    const text = `
AEGIS Vendor Account Credentials
================================

Login URL: ${window.location.origin}/login

Email: ${newCredentials.email}
Password: ${newCredentials.password}

Please save these credentials securely.
The password will not be shown again.
    `.trim();

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendor-credentials-${newCredentials.email}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Filter vendors based on search and status
  const filteredVendors = useMemo(() => {
    return vendors.filter(vendor => {
      // Search filter
      const matchesSearch = !searchTerm.trim() || 
        vendor.vendor_company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.vendor_contact_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.vendor_contact_phone?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Status filter
      const matchesStatus = statusFilter === 'all' || vendor.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [vendors, searchTerm, statusFilter]);

  if (loading) {
    return (
      <div className={`h-full flex items-center justify-center ${isDark ? 'bg-slate-900 text-slate-400' : 'bg-gray-50 text-gray-500'}`}>
        Loading vendors...
      </div>
    );
  }

  return (
    <FeatureGate 
      user={user} 
      featureKey="vendor_management"
      blur={true}
      showUpgradePrompt={true}
      upgradePlanName="Pro"
      customMessage="Vendor management requires the Pro plan or higher."
    >
      <div className={`h-full flex flex-col ${isDark ? 'bg-slate-900 text-slate-100' : 'bg-gray-50 text-slate-900'}`}>
      {/* Header */}
      <div className={`border-b ${isDark ? 'border-slate-800 bg-slate-900/50' : 'border-gray-200 bg-white'} p-6`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>My Vendors</h1>
            <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              Manage vendor accounts for repair assignments
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors"
          >
            <Plus size={18} />
            Add Vendor
          </button>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search Bar */}
          <div className="flex-1 relative">
            <Search 
              size={18} 
              className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} 
            />
            <input
              type="text"
              placeholder="Search vendors by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-lg border text-sm ${
                isDark 
                  ? 'bg-slate-800 border-slate-700 text-slate-200 placeholder-slate-500' 
                  : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          {/* Status Filter */}
          <div className="w-64">
            <SearchableSelect
              options={[
                { id: 'all', name: 'All Vendors' },
                { id: 'active', name: 'Active' },
                { id: 'inactive', name: 'Inactive' }
              ]}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Filter by status..."
              searchPlaceholder="Search status..."
              getOptionLabel={(opt) => opt.name}
              getOptionValue={(opt) => opt.id}
              isDark={isDark}
            />
          </div>
        </div>
      </div>

      {/* Vendors List */}
      <div className="flex-1 overflow-y-auto p-6">
        {filteredVendors.length === 0 ? (
          <div className={`text-center py-12 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
            {vendors.length === 0 ? (
              <>
                <p className="mb-4">No vendors yet. Add your first vendor to get started.</p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors"
                >
                  <Plus size={18} />
                  Add Vendor
                </button>
              </>
            ) : (
              <p>No vendors match your search criteria.</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredVendors.map(vendor => (
              <div
                key={vendor.relationship_id}
                className={`border rounded-xl p-4 ${
                  isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
                } ${vendor.status === 'inactive' ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {vendor.vendor_company_name}
                    </h3>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      {vendor.vendor_contact_email}
                    </p>
                    {vendor.vendor_contact_phone && (
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        {vendor.vendor_contact_phone}
                      </p>
                    )}
                  </div>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      vendor.status === 'active'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}
                  >
                    {vendor.status}
                  </span>
                </div>

                <div className={`text-xs mb-3 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  {vendor.active_repairs_count} active repair{vendor.active_repairs_count !== 1 ? 's' : ''}
                </div>

                <div className="flex gap-2">
                  {vendor.status === 'active' ? (
                    <button
                      onClick={() => handleDeactivate(vendor)}
                      className={`flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        isDark
                          ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                      title="Deactivate"
                    >
                      <PowerOff size={14} />
                      Deactivate
                    </button>
                  ) : (
                    <button
                      onClick={() => handleActivate(vendor)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors bg-green-600 hover:bg-green-500 text-white"
                      title="Activate"
                    >
                      <Power size={14} />
                      Activate
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setSelectedVendor(vendor);
                      setShowResetPasswordModal(true);
                    }}
                    className={`flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      isDark
                        ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                    title="Reset Password"
                  >
                    <Key size={14} />
                    Reset PW
                  </button>
                  <button
                    onClick={() => handleDelete(vendor)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      isDark
                        ? 'bg-red-900/20 hover:bg-red-900/30 text-red-400'
                        : 'bg-red-50 hover:bg-red-100 text-red-600'
                    }`}
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Vendor Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`border rounded-xl w-full max-w-md shadow-2xl ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
            <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Add New Vendor</h2>
              <button onClick={() => setShowAddModal(false)} className="p-1 hover:bg-slate-800 rounded-lg transition-colors">
                <X size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  Vendor Company Name *
                </label>
                <input
                  type="text"
                  value={formData.vendorCompanyName}
                  onChange={(e) => setFormData({ ...formData, vendorCompanyName: e.target.value })}
                  className={`w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500 ${
                    isDark ? 'bg-slate-950 border-slate-700 text-slate-200' : 'bg-white border-gray-200 text-gray-900'
                  } ${errors.vendorCompanyName ? 'border-red-500' : ''}`}
                  placeholder="ABC Repair Shop"
                />
                {errors.vendorCompanyName && <p className="text-red-400 text-xs mt-1">{errors.vendorCompanyName}</p>}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  Contact Email *
                </label>
                <input
                  type="email"
                  value={formData.vendorEmail}
                  onChange={(e) => setFormData({ ...formData, vendorEmail: e.target.value })}
                  className={`w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500 ${
                    isDark ? 'bg-slate-950 border-slate-700 text-slate-200' : 'bg-white border-gray-200 text-gray-900'
                  } ${errors.vendorEmail ? 'border-red-500' : ''}`}
                  placeholder="vendor@example.com"
                />
                {errors.vendorEmail && <p className="text-red-400 text-xs mt-1">{errors.vendorEmail}</p>}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.vendorPhone}
                  onChange={(e) => setFormData({ ...formData, vendorPhone: e.target.value })}
                  className={`w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500 ${
                    isDark ? 'bg-slate-950 border-slate-700 text-slate-200' : 'bg-white border-gray-200 text-gray-900'
                  }`}
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  Initial Password *
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.initialPassword}
                      onChange={(e) => setFormData({ ...formData, initialPassword: e.target.value })}
                      className={`w-full border rounded-lg px-4 py-2.5 pr-10 focus:outline-none focus:border-blue-500 ${
                        isDark ? 'bg-slate-950 border-slate-700 text-slate-200' : 'bg-white border-gray-200 text-gray-900'
                      } ${errors.initialPassword ? 'border-red-500' : ''}`}
                      placeholder="Min. 8 characters"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      {showPassword ? <EyeOff size={16} className="text-slate-400" /> : <Eye size={16} className="text-slate-400" />}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={generatePassword}
                    className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                      isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    Generate
                  </button>
                </div>
                {errors.initialPassword && <p className="text-red-400 text-xs mt-1">{errors.initialPassword}</p>}
              </div>

              <div className={`p-3 rounded-lg ${isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-100'}`}>
                <p className={`text-xs ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>
                  ⓘ You will need to share these credentials with the vendor after creation.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                    isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Creating...' : 'Create Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Credentials Modal */}
      {showCredentialsModal && newCredentials && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`border rounded-xl w-full max-w-md shadow-2xl ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
            <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Vendor Account Created! ✓</h2>
              <button onClick={() => { setShowCredentialsModal(false); setNewCredentials(null); }} className="p-1 hover:bg-slate-800 rounded-lg transition-colors">
                <X size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                Share these credentials with the vendor:
              </p>

              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Login URL:</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={`${window.location.origin}/login`}
                    readOnly
                    className={`flex-1 border rounded-lg px-3 py-2 text-sm ${isDark ? 'bg-slate-950 border-slate-700 text-slate-200' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                  />
                  <button
                    onClick={() => copyToClipboard(`${window.location.origin}/login`, 'url')}
                    className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-slate-800 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'}`}
                  >
                    {copiedField === 'url' ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Email:</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCredentials.email}
                    readOnly
                    className={`flex-1 border rounded-lg px-3 py-2 text-sm ${isDark ? 'bg-slate-950 border-slate-700 text-slate-200' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                  />
                  <button
                    onClick={() => copyToClipboard(newCredentials.email, 'email')}
                    className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-slate-800 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'}`}
                  >
                    {copiedField === 'email' ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Password:</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newCredentials.password}
                      readOnly
                      className={`w-full border rounded-lg px-3 py-2 pr-10 text-sm ${isDark ? 'bg-slate-950 border-slate-700 text-slate-200' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      {showNewPassword ? <EyeOff size={16} className="text-slate-400" /> : <Eye size={16} className="text-slate-400" />}
                    </button>
                  </div>
                  <button
                    onClick={() => copyToClipboard(newCredentials.password, 'password')}
                    className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-slate-800 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'}`}
                  >
                    {copiedField === 'password' ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                  </button>
                </div>
              </div>

              <div className={`p-3 rounded-lg ${isDark ? 'bg-yellow-500/10 border border-yellow-500/20' : 'bg-yellow-50 border border-yellow-100'}`}>
                <p className={`text-xs ${isDark ? 'text-yellow-300' : 'text-yellow-600'}`}>
                  ⚠️ Save these now - the password won't be shown again
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={downloadCredentials}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                    isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  <Download size={16} />
                  Download
                </button>
                <button
                  onClick={() => { setShowCredentialsModal(false); setNewCredentials(null); }}
                  className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedVendor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`border rounded-xl w-full max-w-md shadow-2xl ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
            <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Reset Password</h2>
              <button onClick={() => { setShowResetPasswordModal(false); setSelectedVendor(null); }} className="p-1 hover:bg-slate-800 rounded-lg transition-colors">
                <X size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
              </button>
            </div>

            <form onSubmit={handleResetPassword} className="p-6 space-y-4">
              <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                Reset password for <strong>{selectedVendor.vendor_company_name}</strong>
              </p>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  New Password *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={resetPasswordData.newPassword}
                    onChange={(e) => setResetPasswordData({ newPassword: e.target.value })}
                    className={`flex-1 border rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500 ${
                      isDark ? 'bg-slate-950 border-slate-700 text-slate-200' : 'bg-white border-gray-200 text-gray-900'
                    }`}
                    placeholder="Min. 8 characters"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const password = Array.from({ length: 12 }, () => 
                        'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*'[Math.floor(Math.random() * 70)]
                      ).join('');
                      setResetPasswordData({ newPassword: password });
                    }}
                    className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                      isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    Generate
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowResetPasswordModal(false); setSelectedVendor(null); }}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                    isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </FeatureGate>
  );
}
